import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
  useToast,
} from '../../ui';
import {
  TASK_PRIORITIES,
  TASK_STATUSES,
  blankTaskDraft,
  draftFromTask,
  parseTags,
  tagsToInput,
  totalEffortDays,
  validateTaskDraft,
} from '../../features/tasks/taskModel';
import type { Task, TaskDraft, TaskPriority, TaskStatus } from '../../features/tasks/types';
import { PeopleInput } from '../common/PeopleInput';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';

export interface TaskModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, the modal edits this task; otherwise it creates a new one. */
  task?: Task | null;
  /** Status to preselect for a new task (e.g. the column the user clicked). */
  initialStatus?: TaskStatus;
  /** Persists a new task's draft. */
  onCreate: (draft: TaskDraft) => Promise<unknown>;
  /** Persists edits to an existing task. */
  onSave: (task: Task, draft: TaskDraft) => Promise<unknown>;
}

const STATUS_OPTIONS: SelectOption[] = TASK_STATUSES.map((s) => ({
  value: s.value,
  label: s.label,
}));

const PRIORITY_OPTIONS: SelectOption[] = TASK_PRIORITIES.map((p) => ({
  value: p.value,
  label: p.label,
}));

/**
 * Create/edit task modal. Collects title, description, status, assignee,
 * priority, an optional estimate id and comma-separated tags. Validates via the
 * shared model helper before delegating to the hook-provided persisters.
 */
export function TaskModal({
  open,
  onClose,
  task,
  initialStatus,
  onCreate,
  onSave,
}: TaskModalProps) {
  const { toast } = useToast();
  const editing = !!task;
  const { people } = usePeopleDirectory();

  const [draft, setDraft] = useState<TaskDraft>(blankTaskDraft());
  const [tagsInput, setTagsInput] = useState('');
  const [effortInput, setEffortInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset form whenever the modal opens (for the current task/column).
  useEffect(() => {
    if (!open) return;
    const initial = task
      ? draftFromTask(task)
      : blankTaskDraft(initialStatus);
    setDraft(initial);
    setTagsInput(tagsToInput(initial.tags));
    setEffortInput(initial.effortDays != null ? String(initial.effortDays) : '');
  }, [open, task, initialStatus]);

  const set = <K extends keyof TaskDraft>(key: K, value: TaskDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const close = () => {
    if (saving) return;
    onClose();
  };

  const onSubmit = async () => {
    const effortRaw = effortInput.trim();
    const effortNum = effortRaw === '' ? undefined : Number(effortRaw);
    const effortDays =
      effortNum != null && Number.isFinite(effortNum) && effortNum >= 0
        ? effortNum
        : undefined;
    const candidate: TaskDraft = {
      ...draft,
      tags: parseTags(tagsInput),
      effortDays,
    };
    const problems = validateTaskDraft(candidate);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot save task', description: problems[0] });
      return;
    }
    setSaving(true);
    try {
      if (task) {
        await onSave(task, candidate);
        toast({ tone: 'success', title: 'Task updated', description: candidate.title });
      } else {
        await onCreate(candidate);
        toast({ tone: 'success', title: 'Task created', description: candidate.title });
      }
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: editing ? 'Update failed' : 'Create failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={editing ? 'Edit task' : 'Create task'}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={onSubmit}>
            {editing ? 'Save changes' : 'Create task'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Title"
          value={draft.title}
          placeholder="Implement token refresh"
          onChange={(e) => set('title', e.target.value)}
          autoFocus
        />
        <Textarea
          label="Description"
          value={draft.description ?? ''}
          placeholder="Context, acceptance criteria, links…"
          onChange={(e) => set('description', e.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Status"
            value={draft.status}
            options={STATUS_OPTIONS}
            onChange={(e) => set('status', e.target.value as TaskStatus)}
          />
          <Select
            label="Priority"
            placeholder="No priority"
            value={draft.priority ?? ''}
            options={PRIORITY_OPTIONS}
            onChange={(e) =>
              set('priority', (e.target.value || undefined) as TaskPriority | undefined)
            }
          />
          <PeopleInput
            label="Assignee (alias)"
            value={draft.assignee ?? ''}
            placeholder="jsmith"
            people={people}
            onChange={(alias) => set('assignee', alias)}
          />
          <Input
            label="Estimate id (optional)"
            value={draft.estimateId ?? ''}
            placeholder="Link an estimate later"
            onChange={(e) => set('estimateId', e.target.value)}
          />
        </div>
        <Input
          label="Tags"
          value={tagsInput}
          placeholder="backend, auth, spike"
          hint="Comma-separated. Used to filter the board and list."
          onChange={(e) => setTagsInput(e.target.value)}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Custom effort (days)"
            type="number"
            min={0}
            step={0.5}
            value={effortInput}
            placeholder="e.g. 3"
            hint="Ideal engineering days for this task."
            onChange={(e) => setEffortInput(e.target.value)}
          />
          <div className="flex flex-col justify-end">
            <span className="text-xs font-medium text-zinc-400">
              Total Effort In Days
            </span>
            <span className="mt-1 flex h-9 items-center text-sm tabular-nums text-zinc-200">
              {totalEffortDays({
                effortDays:
                  effortInput.trim() === '' ? undefined : Number(effortInput),
              })}
              d
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default TaskModal;
